/**
 * 登录成功后整页重进时落在哪个地址。
 *
 * 会话在 App 挂载时读一次（bootSession），所以登录后要整页重进；重进前先用
 * `history.replaceState` 把地址改成这里算出来的值（**不产生导航**），再 `location.reload()`
 * ——有且只有一次导航。之前是 `location.replace(...)` 紧接 `location.reload()` 两次连发，
 * 浏览器会取消其中一次，人点了「离开」页面却停在「登录中…」，只能手动刷新。
 *
 * - password：留在当前路径与 hash 上（hash 是当前页），查询串一律丢掉。
 * - discord：notify-hub 把人送回 `…/discord/callback?token=xxx`，一次性 token 必须从地址栏抹掉，
 *   回跳路径也收回根路径。回跳地址本身没有 hash，所以登录页点「Discord 登录」前先把当时的 hash
 *   记进 sessionStorage（stashLandingHash），回来换完票用它（peekLandingHash）——
 *   别人分享的课程链接（`#help-videos/<课程>/<课>`）登录后才能回到那一节。
 */
export function landingUrl(loc: { pathname: string; hash: string }, via: "password" | "discord", stashedHash = ""): string {
  if (via === "discord") return loc.pathname.replace(/\/discord\/callback\/?$/, "/") + safeHash(stashedHash);
  return loc.pathname + loc.hash;
}

/** 只认 `#` 开头、没有换行的 hash；别的一律当没有 */
const safeHash = (hash: string) => (/^#[^\s]*$/.test(hash) && hash.length > 1 ? hash : "");

const HASH_KEY = "aeroplane_admin_landing_hash";
/**
 * 记下的 hash 只在这段时间内有效，过期的不该把下一次登录带到老地方。
 * 30 分钟 = notify-hub 绑定码的有效期：第一次登录要先去 Discord 绑定、再从私信里点登录链接回来，一趟可能要这么久。
 */
const HASH_TTL_MS = 30 * 60 * 1000;
/** 同标签页跨站回来可读取；中台用 window.open 打开后台时，新窗口会继承这份 sessionStorage。 */
export function stashLandingHash(hash: string, now = Date.now()) {
  try {
    if (safeHash(hash)) sessionStorage.setItem(HASH_KEY, JSON.stringify({ hash, at: now }));
    else sessionStorage.removeItem(HASH_KEY);
  } catch { /* 存不了就只是回首页 */ }
}
/**
 * 只读不删：开发模式下 React StrictMode 会把 Gate 的换票 effect 跑两遍，
 * 读一次就删的话第二遍拿到空串、把地址又改回根路径（实测过）。靠 HASH_TTL_MS 过期。
 */
export function peekLandingHash(now = Date.now()): string {
  try {
    const raw = JSON.parse(sessionStorage.getItem(HASH_KEY) ?? "null") as { hash?: unknown; at?: unknown } | null;
    if (!raw || typeof raw.hash !== "string" || typeof raw.at !== "number" || !Number.isFinite(raw.at) || now - raw.at > HASH_TTL_MS || raw.at > now) return "";
    return safeHash(raw.hash);
  } catch { return ""; }
}

/**
 * 「刚登录」标记：登录成功后整页重进，配置台挂载时据此说一句欢迎，让人知道登录成功了、是用哪个身份进来的。
 * 刷新不该再说一遍，所以读一次就删。
 */
const WELCOME_KEY = "aeroplane_admin_welcome";
export const markJustLoggedIn = () => { try { sessionStorage.setItem(WELCOME_KEY, "1"); } catch { /* 没有欢迎也不影响登录 */ } };
export function takeJustLoggedIn(): boolean {
  try {
    const hit = sessionStorage.getItem(WELCOME_KEY) === "1";
    sessionStorage.removeItem(WELCOME_KEY);
    return hit;
  } catch { return false; }
}
